import React, { ReactNode, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useQueryClient } from '@tanstack/react-query';

export type SeoHelmetProps = {
  title: string;
  description: string;
  canonical?: string;
  image?: string;
  ogType?: string;
  children?: ReactNode;
};

export const SeoHelmet = ({
  title,
  description,
  canonical,
  image,
  ogType,
  children,
}: SeoHelmetProps) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    let idleTicks = 0;

    const interval = setInterval(() => {
      const currentTitle = document.title;

      if (
        !currentTitle ||
        currentTitle === 'React App' ||
        currentTitle === '...'
      ) {
        return;
      }

      // Both readiness signals mean the same thing: the title is real AND
      // nothing is in flight. The event used to fire on the first real title
      // alone, and the renderer treats it as terminal - so the snapshot was
      // taken 300ms after the title appeared, while the listing was still
      // loading and the layout's navigation lookups were still pending. That
      // produced week-long cached pages with a head but half a body, and
      // (since 2026-09-21) 503s: PendingHead is still mounted at that moment.
      //
      // Two consecutive idle readings, not one: a chain of dependent queries
      // (category -> its products) has a gap between one finishing and the
      // next starting, and a single reading in that gap looks like "done".
      idleTicks = queryClient.isFetching() === 0 ? idleTicks + 1 : 0;

      if (idleTicks < 2) {
        return;
      }

      const currentDescription = document.querySelector(
        'meta[name="description"]',
        // @ts-ignore
      )?.content;
      const currentCanonical = document.querySelector(
        'link[rel="canonical"]',
        // @ts-ignore
      )?.href;

      window.dispatchEvent(
        new CustomEvent('reactHelmetReady', {
          detail: {
            title: currentTitle,
            description: currentDescription || description,
            canonical: currentCanonical || canonical,
            timestamp: Date.now(),
          },
        }),
      );

      // Lets the prerender service capture the snapshot now instead of
      // waiting out its own timeout (up to 12s per bot hit).
      window.prerenderReady = true;
      clearInterval(interval);
    }, 300);

    return () => clearInterval(interval);
  }, [title, description, canonical, queryClient]);

  const ogImage = image ?? `${window.location.origin}/logo512.png`;
  const ogLocale = process.env.REACT_APP_LANGUAGE === 'LT' ? 'lt_LT' : 'en_GB';

  return (
    <Helmet>
      {title && <title>{title}</title>}
      {description && <meta name="description" content={description} />}
      {canonical && <link rel="canonical" href={canonical} />}
      {title && <meta property="og:title" content={title} />}
      {description && <meta property="og:description" content={description} />}
      <meta property="og:type" content={ogType ?? 'website'} />
      <meta property="og:site_name" content="Partevo" />
      <meta property="og:locale" content={ogLocale} />
      {canonical && <meta property="og:url" content={canonical} />}
      <meta property="og:image" content={ogImage} />
      <meta
        name="twitter:card"
        content={image ? 'summary_large_image' : 'summary'}
      />
      {title && <meta name="twitter:title" content={title} />}
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={ogImage} />
      {children}
    </Helmet>
  );
};

export default SeoHelmet;
